<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core'

interface AccordionItem {
    id: string
    headline: string
    disabled?: boolean
}

interface AccordionProps {
    items?: AccordionItem[]
    headlineTag?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    headlineTagClass?: string
    hasBorder?: boolean
    minHeight?: 'min-h-14' | 'min-h-10'
    headline?: string
    id?: string
    noAccordionOnDesktop?: boolean
    showHeadlineOnDesktop?: boolean
    type?: 'single' | 'multiple'
    collapsible?: boolean
    defaultOpen?: string | string[],
    overflowVisible?: boolean
}

const props = withDefaults(defineProps<AccordionProps>(), {
    items: () => [],
    headlineTag: 'h3',
    hasBorder: true,
    minHeight: 'min-h-14',
    headlineTagClass: '',
    headline: '',
    id: '',
    noAccordionOnDesktop: false,
    showHeadlineOnDesktop: true,
    type: 'single',
    collapsible: true,
    defaultOpen: undefined,
    overflowVisible: false
})

// Support both shared state (via provide/inject) and local state
const sharedState = inject<Ref<string | string[] | null> | null>('sharedAccordionState', null)
const localState = ref<string | string[] | null>(
    props.defaultOpen ?? (props.type === 'multiple' ? [] : null)
)
const openItemId = sharedState ?? localState

// Content height tracking for smooth animations
const contentRefs = ref<Map<string, HTMLElement>>(new Map())
const contentHeights = ref<Map<string, number>>(new Map())

const setContentRef = (id: string, el: HTMLElement | null) => {
    if (el) {
        contentRefs.value.set(id, el)
        updateContentHeight(id)

        // Set up resize observer for this element using VueUse
        useResizeObserver(el, () => {
            updateContentHeight(id)
        })
    } else {
        contentRefs.value.delete(id)
        contentHeights.value.delete(id)
    }
}

const updateContentHeight = (id: string) => {
    const el = contentRefs.value.get(id)
    if (el) {
        contentHeights.value.set(id, el.scrollHeight)
    }
}

const isOpen = (id: string): boolean => {
    if (props.type === 'multiple' && Array.isArray(openItemId.value)) {
        return openItemId.value.includes(id)
    }
    return openItemId.value === id
}

const emit = defineEmits<{
    toggle: [id: string, isOpen: boolean]
}>()

const toggle = (id: string, disabled?: boolean) => {
    if (disabled) return

    if (props.type === 'multiple') {
        const currentOpen = Array.isArray(openItemId.value) ? openItemId.value : []
        if (currentOpen.includes(id)) {
            openItemId.value = currentOpen.filter((itemId) => itemId !== id)
            emit('toggle', id, false)
        } else {
            openItemId.value = [...currentOpen, id]
            emit('toggle', id, true)
        }
    } else {
        if (props.collapsible && openItemId.value === id) {
            openItemId.value = null
            emit('toggle', id, false)
        } else {
            openItemId.value = id
            emit('toggle', id, true)
        }
    }
}

const getContentHeight = (id: string): string => {
    if (isOpen(id)) {
        const height = contentHeights.value.get(id)
        return height ? `${height}px` : 'auto'
    }
    return '0px'
}
</script>

<template>
    <div>
        <!-- Items-based accordion (multiple accordions) -->
        <div
            v-for="item in items"
            :key="item.id"
            :class="[
                noAccordionOnDesktop ? 'lg:border-b-0' : '',
                hasBorder ? 'border-b border-border last:border-b-0' : 'border-0'
            ]"
        >
            <FoundationHeadline
                :tag="headlineTag"
                :class="[
                    'flex justify-between items-center',
                    noAccordionOnDesktop ? 'lg:py-0 lg:min-h-auto' : '',
                    hasBorder ? 'py-2' : '',
                    minHeight ? minHeight : ''
                ]"
            >
                <button
                    :id="`accordion-header-${item.id}`"
                    type="button"
                    class="flex-1 flex justify-between items-center cursor-pointer text-left"
                    :class="[
                        headlineTagClass ? headlineTagClass : 'h4 font-bold uppercase',
                        noAccordionOnDesktop && !showHeadlineOnDesktop ? 'lg:sr-only' : '',
                        noAccordionOnDesktop && showHeadlineOnDesktop ? 'lg:mb-4' : '',
                        noAccordionOnDesktop ? 'lg:cursor-default lg:pointer-events-none' : '',
                        item.disabled ? 'opacity-50 cursor-not-allowed' : ''
                    ]"
                    :aria-expanded="isOpen(item.id) ? 'true' : 'false'"
                    :aria-controls="`accordion-content-${item.id}`"
                    :aria-disabled="item.disabled ? 'true' : 'false'"
                    :disabled="item.disabled"
                    :tabindex="noAccordionOnDesktop && showHeadlineOnDesktop ? '-1' : '0'"
                    @click="toggle(item.id, item.disabled)"
                >
                    <span>{{ item.headline }}</span>

                    <span
                        class="w-10 h-10 flex justify-center items-center shrink-0"
                        :class="[
                            noAccordionOnDesktop ? 'lg:hidden' : ''
                        ]"
                        aria-hidden="true"
                    >
                        <FoundationIcon
                            v-if="!isOpen(item.id)"
                            name="plus"
                            size="md"
                            class="shrink-0"
                        />
                        <FoundationIcon
                            v-else
                            name="minus"
                            size="md"
                            class="shrink-0"
                        />
                    </span>
                </button>
            </FoundationHeadline>

            <div
                :id="`accordion-content-${item.id}`"
                class="transition-[max-height,opacity] duration-300 ease-in-out"
                :class="[
                    isOpen(item.id) ? 'opacity-100' : 'opacity-0',
                    noAccordionOnDesktop ? 'lg:!max-h-none lg:!overflow-visible lg:!transition-none lg:!opacity-100' : '',
                    !overflowVisible ? 'overflow-hidden' : 'overflow-visible'
                ]"
                :style="{ maxHeight: getContentHeight(item.id) }"
                role="region"
                :aria-labelledby="`accordion-header-${item.id}`"
                :aria-hidden="!isOpen(item.id) && !noAccordionOnDesktop ? 'true' : 'false'"
            >
                <div
                    :ref="(el) => setContentRef(item.id, el as HTMLElement)"
                    :data-accordion-id="item.id"
                >
                    <slot :name="item.id" :is-open="isOpen(item.id)" :item="item" />
                </div>
            </div>
        </div>
    </div>
</template>
